import { InputRule, mergeAttributes, Node, nodePasteRule } from '@tiptap/core'

// Matches: #123  ##123  #123-6  #123#note-6
// Requires whitespace or start-of-string before, whitespace or punctuation after.
// Does NOT match when preceded by '!' (escape prefix).
const ISSUE_REF_RE = /(?<=\s|^)(##?(\d+)(?:-(\d+)|#note-(\d+))?)(?=[\s.,;:!?)\]]|$)/gm

export const IssueRef = Node.create({
  name: 'redmineIssueReference',
  priority: 101,
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      raw: { default: '' }, // exact original string, used for markdown roundtrip
      issueId: { default: '' },
      title: { default: '' },
      fullDetail: { default: false }, // true when prefix is ##
      noteRef: { default: null }, // '6' for #123-6 or #123#note-6
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-issue-ref]',
        getAttrs: el => ({
          raw: el.getAttribute('data-issue-ref') || el.textContent || '',
          issueId: el.getAttribute('data-issue-id'),
          title: el.getAttribute('title') || '',
          fullDetail: el.hasAttribute('data-full-detail'),
          noteRef: el.getAttribute('data-note-ref') || null,
        }),
      },
    ]
  },

  renderHTML({ node }) {
    return [
      'span',
      mergeAttributes({
        'data-issue-ref': node.attrs.raw,
        'data-issue-id': node.attrs.issueId,
        ...(node.attrs.title ? { title: node.attrs.title } : {}),
        ...(node.attrs.fullDetail ? { 'data-full-detail': '' } : {}),
        ...(node.attrs.noteRef ? { 'data-note-ref': node.attrs.noteRef } : {}),
        'class': 'issue-ref-node',
      }),
      node.attrs.raw || `#${node.attrs.issueId}`,
    ]
  },

  renderMarkdown(node) {
    if (!node.attrs)
      return ''
    return node.attrs.raw || `#${node.attrs.issueId}`
  },

  addInputRules() {
    // Only convert once the reference is terminated by a space/newline —
    // never mid-digit, so the issue-suggestion popup keeps matching while typing.
    return [
      new InputRule({
        // Browsers insert U+00A0 (nbsp) instead of a plain space for
        // trailing whitespace in contenteditable — accept both.
        find: /(?<!!)(?:^|\s)(##?(\d+)(?:-(\d+)|#note-(\d+))?)[ \u00A0\n]$/,
        handler: ({ state, range, match }) => {
          const raw = match[1]
          const issueId = match[2]
          const noteRef = match[3] ?? match[4] ?? null
          const fullDetail = raw.startsWith('##')
          const end = range.to - 1 // exclude the trailing space/newline that triggered the match
          const start = end - raw.length
          state.tr.replaceWith(start, end, this.type.create({ raw, issueId, fullDetail, noteRef }))
        },
      }),
    ]
  },

  addPasteRules() {
    const origin = window.location.origin
    return [
      nodePasteRule({
        find: new RegExp(
          `${RegExp.escape(origin)}/(?:projects/[^/]+/)?issues/(\\d+)(?:#note-(\\d+))?`,
          'g',
        ),
        type: this.type,
        getAttributes: (match) => {
          const issueId = match[1]
          const noteRef = match[2] ?? null
          const raw = noteRef ? `#${issueId}#note-${noteRef}` : `#${issueId}`
          return { raw, issueId, noteRef, fullDetail: false }
        },
      }),
    ]
  },

  markdownTokenName: 'redmineIssueReference',

  markdownTokenizer: {
    name: 'redmineIssueReference',
    level: 'inline',
    start: (src) => {
      const idx = src.indexOf('#')
      // Skip '!' escaped references
      if (idx > 0 && src[idx - 1] === '!')
        return idx + 1
      return idx
    },
    tokenize: (src) => {
      ISSUE_REF_RE.lastIndex = 0
      const m = ISSUE_REF_RE.exec(src)
      if (!m)
        return
      const raw = m[1]
      const issueId = m[2]
      const noteRef = m[3] ?? m[4] ?? null
      const fullDetail = raw.startsWith('##')
      return {
        type: 'redmineIssueReference',
        raw: m[0], // includes leading whitespace consumed by lookbehind
        issueId,
        raw_text: raw,
        noteRef,
        fullDetail,
        tokens: [],
      } as any
    },
  },

  parseMarkdown(token) {
    const t = token as any
    return {
      type: 'redmineIssueReference',
      attrs: {
        raw: t.raw_text ?? `#${t.issueId}`,
        issueId: t.issueId,
        noteRef: t.noteRef ?? null,
        fullDetail: t.fullDetail ?? false,
      },
    }
  },
})
